/**
 * ReorderSuggestions.jsx — Auto-draft Purchase Orders from stockout-risk
 * forecasting + lead times. Operators tick the lines they want, hit
 * "Draft POs" and the backend creates one pending Receipt per supplier.
 *
 * Surfaces under Inbound → Reorder Suggestions for admin/manager only.
 *
 * Notes:
 *  - The recommended_qty math lives on the backend so the same numbers
 *    appear no matter which client reads them.
 *  - Operators can override qty inline before drafting (we send the
 *    edited qty back).
 *  - Tabs by supplier — each tab has its own "Draft PO for {supplier}"
 *    button so partial work is supported (you don't have to act on
 *    every supplier in one go).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  getReorderSuggestions, draftPosFromReorderSuggestions,
} from '../lib/api';
import {
  TrendDown, Package, Lightning, ArrowRight, FileArrowDown,
  WarningCircle, ChartLineDown, Storefront,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const RISK_STYLES = {
  high:   { dot: 'bg-red-600',   chip: 'bg-red-100 text-red-800 border-red-200',   label: 'High' },
  medium: { dot: 'bg-amber-500', chip: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Medium' },
  low:    { dot: 'bg-blue-500',  chip: 'bg-blue-100 text-blue-800 border-blue-200',     label: 'Low' },
};

export default function ReorderSuggestions() {
  const [data, setData] = useState({ groups: [], rows: [] });
  const [loading, setLoading] = useState(true);
  const [drafting, setDrafting] = useState(false);

  // Knobs
  const [leadTime, setLeadTime] = useState(7);
  const [target, setTarget] = useState(30);

  // Per-row state — keyed by sku_id
  const [selected, setSelected] = useState({});
  const [overrides, setOverrides] = useState({}); // sku_id -> qty override

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getReorderSuggestions({ lead_time_days: leadTime, target_days_cover: target });
      setData(res.data);
      // Default-select every row so the operator can quickly accept the
      // whole batch with one click.
      const allSelected = {};
      for (const r of res.data.rows || []) allSelected[r.sku_id] = true;
      setSelected(allSelected);
      setOverrides({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const totalSelected = useMemo(() => {
    return (data.rows || []).reduce((acc, r) => {
      if (!selected[r.sku_id]) return acc;
      const qty = overrides[r.sku_id] != null ? overrides[r.sku_id] : r.recommended_qty;
      acc.lines += 1;
      acc.qty += qty;
      acc.cost += (r.unit_price || 0) * qty;
      return acc;
    }, { lines: 0, qty: 0, cost: 0 });
  }, [data.rows, selected, overrides]);

  const setRowQty = (sku_id, val) => {
    const n = parseInt(val, 10);
    setOverrides((o) => ({ ...o, [sku_id]: Number.isFinite(n) && n >= 0 ? n : 0 }));
  };

  const toggleRow = (sku_id) => {
    setSelected((s) => ({ ...s, [sku_id]: !s[sku_id] }));
  };

  const toggleSupplier = (group, value) => {
    setSelected((s) => {
      const next = { ...s };
      for (const r of group.rows) next[r.sku_id] = value;
      return next;
    });
  };

  const draftForSupplier = async (group) => {
    const items = group.rows
      .filter((r) => selected[r.sku_id])
      .map((r) => ({
        sku_id: r.sku_id,
        supplier_name: group.supplier_name,
        recommended_qty: overrides[r.sku_id] != null ? overrides[r.sku_id] : r.recommended_qty,
      }))
      .filter((it) => it.recommended_qty > 0);
    if (!items.length) {
      toast.error(`No lines selected for ${group.supplier_name}`);
      return;
    }
    setDrafting(true);
    try {
      const res = await draftPosFromReorderSuggestions({ items });
      toast.success(res.data?.message || 'PO drafted', {
        description: res.data?.receipts?.[0]?.receipt_number,
        action: res.data?.receipts?.[0] ? {
          label: 'Open',
          onClick: () => { window.location.href = '/receiving'; },
        } : undefined,
      });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Draft failed');
    } finally {
      setDrafting(false);
    }
  };

  const draftAll = async () => {
    const items = (data.rows || [])
      .filter((r) => selected[r.sku_id])
      .map((r) => ({
        sku_id: r.sku_id,
        supplier_name: r.supplier_name,
        recommended_qty: overrides[r.sku_id] != null ? overrides[r.sku_id] : r.recommended_qty,
      }))
      .filter((it) => it.recommended_qty > 0);
    if (!items.length) { toast.error('No lines selected'); return; }
    setDrafting(true);
    try {
      const res = await draftPosFromReorderSuggestions({ items });
      toast.success(res.data?.message || 'POs drafted', {
        description: `Created ${res.data?.receipts?.length || 0} receipt(s)`,
        action: { label: 'Open Receiving', onClick: () => { window.location.href = '/receiving'; } },
      });
      fetchData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Draft failed');
    } finally {
      setDrafting(false);
    }
  };

  const hasData = (data.groups || []).length > 0;

  return (
    <Layout>
      <div data-testid="reorder-suggestions-page" className="p-4 md:p-6 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-muted-foreground text-xs font-semibold uppercase tracking-widest">
              <ChartLineDown size={14} weight="fill" /> Inbound · Auto-drafted from stockout risk
            </div>
            <h1 className="text-3xl font-bold tracking-tight mt-1">Reorder Suggestions</h1>
            <p className="text-muted-foreground text-sm mt-1">
              Per-SKU buy quantities sized to bring cover back up to {target} days after the new stock lands. Tick the lines you want and we'll draft one pending Receipt per supplier.
            </p>
          </div>
          {hasData && (
            <Button
              onClick={draftAll}
              disabled={drafting || totalSelected.lines === 0}
              data-testid="draft-all-btn"
              className="bg-brand hover:bg-brand/90 rounded-none"
            >
              <Lightning size={16} weight="fill" className="mr-2" />
              {drafting ? 'Drafting…' : `Draft ${totalSelected.lines} line${totalSelected.lines === 1 ? '' : 's'}`}
            </Button>
          )}
        </div>

        <div className="bg-card border border-border rounded-lg p-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="overline">Lead time (days)</Label>
            <Input
              type="number"
              min={1}
              value={leadTime}
              onChange={(e) => setLeadTime(parseInt(e.target.value, 10) || 1)}
              data-testid="lead-time-input"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="overline">Target days of cover</Label>
            <Input
              type="number"
              min={1}
              value={target}
              onChange={(e) => setTarget(parseInt(e.target.value, 10) || 1)}
              data-testid="target-cover-input"
              className="mt-1"
            />
          </div>
          <div className="md:col-span-2 flex items-end">
            <Button
              variant="outline"
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-btn"
              className="rounded-none w-full md:w-auto"
            >
              {loading ? 'Loading…' : 'Recalculate'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-muted rounded-md animate-pulse" />
            ))}
          </div>
        ) : !hasData ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="reorder-empty">
            <Package size={48} className="mx-auto mb-4 text-muted-foreground/40" />
            <p className="text-muted-foreground max-w-md mx-auto">
              No reorder suggestions right now — every SKU has more than {leadTime * 3} days of cover at the current burn rate.
              Loosen the lead time / target days knobs above to widen the net, or come back tomorrow.
            </p>
          </div>
        ) : (
          <>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-sm flex items-center gap-3 flex-wrap">
              <WarningCircle size={20} weight="fill" className="text-emerald-700 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-bold text-emerald-900">
                  {data.rows.length} SKU{data.rows.length === 1 ? '' : 's'} flagged across {data.groups.length} supplier{data.groups.length === 1 ? '' : 's'}
                </div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  Selected: {totalSelected.lines} line{totalSelected.lines === 1 ? '' : 's'} · {totalSelected.qty} units · est. ${totalSelected.cost.toFixed(2)}
                </div>
              </div>
              <Link to="/receiving" className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:underline">
                Receiving queue <ArrowRight size={12} className="inline" />
              </Link>
            </div>

            <Tabs defaultValue={data.groups[0]?.supplier_name} className="w-full">
              <TabsList className="flex-wrap h-auto justify-start gap-1 bg-transparent p-0 mb-3">
                {data.groups.map((g) => (
                  <TabsTrigger
                    key={g.supplier_name}
                    value={g.supplier_name}
                    data-testid={`supplier-tab-${g.supplier_name}`}
                    className="data-[state=active]:bg-brand data-[state=active]:text-white border border-border rounded-none"
                  >
                    <Storefront size={12} weight="bold" className="mr-1" />
                    {g.supplier_name}
                    <span className="ml-2 text-[10px] font-bold opacity-70">{g.rows.length} · {g.total_qty}u</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {data.groups.map((g) => {
                const allSelectedForSupplier = g.rows.every((r) => selected[r.sku_id]);
                return (
                  <TabsContent key={g.supplier_name} value={g.supplier_name} className="mt-0">
                    <div className="bg-card border border-border rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/30 flex-wrap gap-2">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={allSelectedForSupplier}
                            onChange={(e) => toggleSupplier(g, e.target.checked)}
                            className="h-4 w-4"
                            data-testid={`select-all-${g.supplier_name}`}
                          />
                          <div>
                            <div className="font-bold">{g.supplier_name}</div>
                            <div className="text-xs text-muted-foreground">
                              {g.rows.length} line{g.rows.length === 1 ? '' : 's'} · {g.total_qty} units · est. ${g.total_cost.toFixed(2)}
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => draftForSupplier(g)}
                          disabled={drafting}
                          data-testid={`draft-supplier-${g.supplier_name}`}
                          className="bg-brand hover:bg-brand/90 rounded-none text-xs"
                          size="sm"
                        >
                          <FileArrowDown size={14} className="mr-1" />
                          Draft PO for {g.supplier_name}
                        </Button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full table-dense">
                          <thead>
                            <tr>
                              <th className="w-10"></th>
                              <th>SKU</th>
                              <th className="text-right">On Hand</th>
                              <th className="text-right">Daily Burn</th>
                              <th className="text-right">Days Left</th>
                              <th>Risk</th>
                              <th className="text-right">Recommend</th>
                              <th className="text-right">Est. Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.rows.map((r) => {
                              const qty = overrides[r.sku_id] != null ? overrides[r.sku_id] : r.recommended_qty;
                              const cost = (r.unit_price || 0) * qty;
                              const risk = RISK_STYLES[r.risk_level] || RISK_STYLES.low;
                              return (
                                <tr key={r.sku_id} data-testid={`reorder-row-${r.sku_code}`} className="hover:bg-muted/40">
                                  <td>
                                    <input
                                      type="checkbox"
                                      checked={!!selected[r.sku_id]}
                                      onChange={() => toggleRow(r.sku_id)}
                                      className="h-4 w-4"
                                      data-testid={`select-row-${r.sku_code}`}
                                    />
                                  </td>
                                  <td>
                                    <div className="font-mono font-bold">{r.sku_code}</div>
                                    <div className="text-xs text-muted-foreground truncate max-w-[260px]">{r.name}</div>
                                  </td>
                                  <td className="font-mono text-right">{r.current_qty}</td>
                                  <td className="font-mono text-right">{r.daily_burn}</td>
                                  <td className="font-mono text-right">
                                    <span className="font-bold">{r.days_left}d</span>
                                    <div className="text-[10px] text-muted-foreground">stockout {r.projected_stockout_date}</div>
                                  </td>
                                  <td>
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${risk.chip}`}>
                                      <span className={`w-2 h-2 rounded-full ${risk.dot}`} />
                                      {risk.label}
                                    </span>
                                  </td>
                                  <td className="text-right">
                                    <Input
                                      type="number"
                                      min={0}
                                      value={qty}
                                      onChange={(e) => setRowQty(r.sku_id, e.target.value)}
                                      data-testid={`row-qty-${r.sku_code}`}
                                      className="text-right font-mono w-24 ml-auto"
                                    />
                                  </td>
                                  <td className="font-mono text-right">
                                    {r.unit_price ? `$${cost.toFixed(2)}` : <span className="text-muted-foreground">—</span>}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          </>
        )}
      </div>
    </Layout>
  );
}
